import { StandardLink, OnlyForScreenReader } from '@brandfolder/react';
import { decode } from 'html-entities';
import React, { FunctionComponent, useState } from 'react';

import { I18nProviderWrapper } from '@components/common/I18nProviderWrapper';

import CreateFtpIngestForm from './form';
import { IngestSource, Map } from './FtpIngestTypes';
import Row from './row';

interface FtpIngestManagementProps {
  ingestSources: IngestSource[];
  sections: Map;
  collections: Map;
  gettyClient: boolean;
}

type FtpIngestFormProps = FtpIngestManagementProps & { setShowForm: SetStateDispatch<boolean> };

type FtpIngestRowsProps = FtpIngestManagementProps & { setIngestSources: SetStateDispatch<IngestSource[]> };

const FtpIngestFormContainer: FunctionComponent<FtpIngestFormProps> = ({
  collections,
  gettyClient,
  sections,
  setShowForm
}) => (
  <div id="manage-ftp-ingest">
    <div
      className="ftp-ingests--cancel-edit"
      onClick={(): void => setShowForm(false)}
      onKeyPress={(): void => setShowForm(false)}
      role="button"
      tabIndex={0}
    >
      <span className="ftp-ingest-back-indicator bff-arrow-left" />
      <span className="ftp-ingest-back-indicator">{bfTranslate('Back')}</span>
    </div>
    <CreateFtpIngestForm
      collections={collections}
      gettyClient={gettyClient}
      sections={sections}
    />
  </div>
);

const FtpIngestRows: FunctionComponent<FtpIngestRowsProps> = (props) => {
  const { collections, gettyClient, ingestSources, sections, setIngestSources } = props;
  const widthClass = gettyClient ? 'quarter-width' : 'third-width';
  return ingestSources.length > 0 ? (
    <>
      <div className="ftp-ingest-description">
        {!gettyClient && (
          <p className="ftp-ingest-description--content">
            {`${bfTranslate('Ingest assets into Brandfolder using:')} `}
            <strong>sftp://files.brandfolder.io</strong>
            <br />
            <strong>{bfTranslate('Port 22')}</strong>
          </p>
        )}
      </div>
      <div id="ftp-ingest-list">
        <div className="headers">
          <div className="headers-container">
            <h3 className={widthClass}>{bfTranslate('FTP Name')}</h3>
            <h3 className={widthClass}>{bfTranslate('Add to section')}</h3>
            <h3 className={widthClass}>{bfTranslate('Also add to Collection')}</h3>
            {gettyClient && <h3 className={widthClass}>{bfTranslate('Ingest URL')}</h3>}
          </div>
        </div>
        {ingestSources.map((source) => (
          <Row
            key={source.key}
            collections={collections}
            gettyClient={gettyClient}
            sections={sections}
            setIngestSources={setIngestSources}
            source={source}
          />
        ))}
      </div>
    </>
  ) : (
    <div id="ftp-ingest-list">
      <span className="no-ftp-ingests-message">
        {`${decode(BFG.resource.name)} ${bfTranslate('does not have any FTP ingests')}`}
      </span>
    </div>
  );
};

const FtpIngestManagement: FunctionComponent<FtpIngestManagementProps> = (props) => {
  const [showForm, setShowForm] = useState(false);
  const [ingestSources, setIngestSources] = useState(props.ingestSources);
  return (
    <I18nProviderWrapper>
      <div className="manage-ftp-ingests">
        <div className="header">
          <h2>{bfTranslate('FTP Ingest')}</h2>
          <p>
            {bfTranslate('This feature will be deprecated on October 1st, 2022')}&nbsp;
            <StandardLink href={`/${BFG.resource?.slug}/bulk_management#sftp_ingest`} target='_blank'>
              {bfTranslate('Please use the new SFTP feature.')}
              <OnlyForScreenReader>{bfTranslate('Opens in a new tab.')}</OnlyForScreenReader>
            </StandardLink>
          </p>
        </div>
        <hr />
        {showForm ? (
          <FtpIngestFormContainer
            {...props}
            setShowForm={setShowForm}
          />
        ) : (
          <FtpIngestRows
            {...props}
            ingestSources={ingestSources}
            setIngestSources={setIngestSources}
          />
        )}
      </div>
    </I18nProviderWrapper>
  );
};

export default FtpIngestManagement;
